import React from 'react';
import { format, parseISO } from 'date-fns';
import {
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import Modal from '../../common/Modal';
import type { CalendarEvent } from '../../../types/integrations';

interface LearnerEventDetailModalProps {
  event: CalendarEvent;
  onClose: () => void;
}

const formatWhen = (event: CalendarEvent): string => {
  try {
    if (event.all_day) {
      const startDate = parseISO(event.start);
      const endDate = parseISO(event.end);
      const same = format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd');
      return same
        ? `${format(startDate, 'PPP')} · All day`
        : `${format(startDate, 'PPP')} → ${format(endDate, 'PPP')} · All day`;
    }
    const start = parseISO(event.start);
    const end = parseISO(event.end);
    const sameDay = format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd');
    if (sameDay) {
      return `${format(start, 'PPPP')} · ${format(start, 'p')} – ${format(end, 'p')}`;
    }
    return `${format(start, 'PPP p')} → ${format(end, 'PPP p')}`;
  } catch {
    return `${event.start} → ${event.end}`;
  }
};

const LearnerEventDetailModal: React.FC<LearnerEventDetailModalProps> = ({
  event,
  onClose,
}) => {
  const whenText = formatWhen(event);
  const calendarLabel = event.calendar_name || event.calendar_id;

  return (
    <Modal isOpen onClose={onClose} title="" size="xl">
      <div className="space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                event.is_tereiko
                  ? 'bg-primary-500/10 text-primary-600 dark:text-primary-300'
                  : 'bg-surface-elevated text-text-secondary'
              }`}
            >
              {event.is_tereiko ? 'Tereiko' : calendarLabel}
            </span>
            {event.status === 'cancelled' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-error-500/10 text-error-600 dark:text-error-300">
                Canceled
              </span>
            )}
          </div>
          <h2 className="text-xl font-semibold text-text-primary leading-snug">
            {event.summary || '(No title)'}
          </h2>
        </div>

        <div className="space-y-3 pt-1">
          <div className="flex items-start gap-3">
            <ClockIcon className="w-5 h-5 text-text-tertiary shrink-0 mt-0.5" />
            <p className="text-sm text-text-primary">{whenText}</p>
          </div>

          {event.location && (
            <div className="flex items-start gap-3">
              <MapPinIcon className="w-5 h-5 text-text-tertiary shrink-0 mt-0.5" />
              <p className="text-sm text-text-primary break-words">{event.location}</p>
            </div>
          )}

          {!event.is_tereiko && calendarLabel && (
            <div className="flex items-start gap-3">
              <CalendarIcon className="w-5 h-5 text-text-tertiary shrink-0 mt-0.5" />
              <p className="text-sm text-text-secondary">{calendarLabel}</p>
            </div>
          )}

          {event.description && (
            <div className="pt-2 border-t border-border-primary/15">
              <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-1.5">
                Description
              </p>
              <p className="text-sm text-text-primary whitespace-pre-wrap break-words">
                {event.description}
              </p>
            </div>
          )}
        </div>

        {event.html_link && (
          <div className="pt-2 flex justify-end">
            <a
              href={event.html_link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline btn-sm inline-flex items-center gap-2"
            >
              Open in Google Calendar
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LearnerEventDetailModal;
